/**
 * `<EventJoinLinkCard/>` - "Event join link" on the Map members page (#2658).
 *
 * The lead makes one Telegram link for an event and posts it once in the group
 * chat. Each helper who taps it and presses Start joins the team, and the lead
 * gets a Telegram message with "Not them? Remove" for each join. Roster teams
 * keep one link per person on the People page.
 *
 * Phone first: one column, full-width controls, the link wraps.
 */
import { useState } from "react";

import { Button } from "../../components/ui";
import { Input } from "../../components/ui/Input";
import {
  type EventJoinLink,
  useCreateEventJoinLink,
  useEventJoinLinks,
  useRevokeEventJoinLink,
} from "./useEventJoinLinks";

const STATE_LABEL: Record<EventJoinLink["state"], string> = {
  live: "Open",
  expired: "Expired",
  revoked: "Revoked",
  full: "Full",
};

/** The server keeps only a hash of the link, so this browser keeps the link it made. */
const urlKey = (linkId: string) => `tarii.eventJoinLink.${linkId}`;

function savedUrl(linkId: string): string | null {
  try {
    return window.localStorage.getItem(urlKey(linkId));
  } catch {
    return null;
  }
}

function untilLabel(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

export function EventJoinLinkCard({ teamId, teamName }: { teamId: string; teamName: string }) {
  const links = useEventJoinLinks(teamId, true);
  const create = useCreateEventJoinLink(teamId);
  const revoke = useRevokeEventJoinLink(teamId);
  const [label, setLabel] = useState("");
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // A member (403) or a team without the link (404) sees no card.
  if (links.isError) return null;

  const onCopy = async (link: EventJoinLink) => {
    try {
      const url = link.url ?? savedUrl(link.id);
      if (!url) return;
      await navigator.clipboard.writeText(url);
      setCopiedId(link.id);
    } catch {
      setCopiedId(null);
    }
  };

  const onCreate = () => {
    create.mutate(label.trim() || teamName, {
      onSuccess: (made) => {
        setLabel("");
        if (made.url) {
          try {
            window.localStorage.setItem(urlKey(made.id), made.url);
          } catch {
            // Storage may be blocked. The link is then shown only on this screen.
          }
        }
      },
    });
  };

  return (
    <section
      data-testid="event-join-link-card"
      className="mt-6 rounded-xl border border-border bg-bg-elevated p-4"
    >
      <h2 className="text-sm font-semibold text-fg">Event join link</h2>
      <p className="mt-1 text-sm text-fg-muted">
        Post one link in your group chat. Each helper who taps it and presses Start joins this team.
        You get a message for each join, with a button to remove a wrong one.
      </p>

      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <Input
          aria-label="Event name"
          data-testid="event-join-label"
          maxLength={120}
          value={label}
          placeholder={teamName}
          onChange={(e) => setLabel(e.target.value)}
        />
        <Button
          type="button"
          data-testid="event-join-create"
          className="w-full sm:w-auto"
          onClick={onCreate}
          disabled={create.isPending}
        >
          Create link
        </Button>
      </div>
      {create.isError ? (
        <p role="alert" className="mt-2 text-sm text-danger">
          We could not make a link. Telegram may be off for this team.
        </p>
      ) : null}

      <ul className="mt-3 flex flex-col gap-3" data-testid="event-join-links">
        {(Array.isArray(links.data) ? links.data : []).map((link) => (
          <li
            key={link.id}
            data-testid="event-join-link"
            className="rounded-lg border border-border bg-bg-subtle p-3"
          >
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm font-medium text-fg">{link.label}</span>
              <span className="shrink-0 text-xs text-fg-muted">{STATE_LABEL[link.state]}</span>
            </div>
            {(link.url ?? savedUrl(link.id)) ? (
              <p data-testid="event-join-url" className="mt-1 break-all text-xs text-fg-muted">
                {link.url ?? savedUrl(link.id)}
              </p>
            ) : (
              <p className="mt-1 text-xs text-fg-muted">
                This browser did not make the link. Make a new link to copy it.
              </p>
            )}
            <p className="mt-1 text-xs text-fg-muted" data-testid="event-join-count">
              {link.member_count} joined of {link.max_joins} · until {untilLabel(link.expires_at)}
            </p>
            <div className="mt-2 flex gap-2">
              {(link.url ?? savedUrl(link.id)) ? (
                <Button
                  type="button"
                  variant="secondary"
                  className="flex-1 sm:flex-none"
                  data-testid="event-join-copy"
                  onClick={() => void onCopy(link)}
                >
                  {copiedId === link.id ? "Copied" : "Copy link"}
                </Button>
              ) : null}
              {link.state === "live" ? (
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 sm:flex-none"
                  data-testid="event-join-revoke"
                  onClick={() => revoke.mutate(link.id)}
                  disabled={revoke.isPending}
                >
                  Revoke
                </Button>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
